/**
 * InfoTip — an ⓘ beside a heading, a pill or a row, with the explanation in
 * its tooltip (docs/features/journey-visual-language/01_proposal.md §4, "Tab
 * content"): why a line is fixed, how the order is invoiced, why an invoice
 * is held. Explanations live here, not as paragraphs on the page.
 *
 * It is a button so keyboard users reach it; its accessible name says what
 * it explains and its description is the explanation itself.
 */
import { useId, type ReactNode } from "react";
import { Info, Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../primitives/tooltip";
import { cn } from "../primitives/utils";

export function InfoTip({
  label,
  children,
  icon = "info",
  className,
}: {
  /** What it explains, as its accessible name: "Why .01 is fixed". */
  label: string;
  /** The explanation: a sentence or two. */
  children: ReactNode;
  /** A lock for a locked row; the ⓘ otherwise. */
  icon?: "info" | "lock";
  className?: string;
}) {
  const descriptionId = useId();
  const Icon = icon === "lock" ? Lock : Info;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-describedby={descriptionId}
          className={cn(
            "inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          <Icon aria-hidden className="size-3.5" />
          <span id={descriptionId} className="sr-only">
            {children}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}
